




































































































































































































































































import Vue from 'vue';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';

import mixins from '@/utils/mixins';
import authMixin from '@/mixins/auth';
import snackbarMixin from '@/mixins/snackbar';

import { ALForm } from '@/common/component.types';
import { ISystemError, SystemError } from '@/models/error/index';
import {
  Contract,
  IContract,
  IContractData,
  IQuote,
  QuoteState
} from '@/models';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import CurrencyPicker from '@/components/currency/CurrencyPicker.vue';
import DatePicker from '@/components/common/DatePicker.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import QuotePicker from '@/components/quote-common/QuotePicker.vue';
import QuotePaymentTermCodePicker from '@/components/quotes/templates/payment-terms/QuotePaymentTermCodePicker.vue';

interface Options extends InstanceType<typeof Vue> {
  $refs: {
    contractForm: ALForm;
    startDatePicker: InstanceType<typeof DatePicker> & {
      $refs: {
        datePicker: ALForm;
      };
    };
    endDatePicker: InstanceType<typeof DatePicker> & {
      $refs: {
        datePicker: ALForm;
      };
    };
  };
}

/**
 * @summary When updating STEPS update {FirstStep} if other enum is made 1
 * @enum STEPS
 */
enum STEPS {
  QuoteSelector = 1,
  ContractBuilder = 2
}

export default mixins(authMixin, snackbarMixin)
  .extend<Options>({ functional: false })
  .extend({
    name: 'ContractBuilderDialog',

    components: {
      ALDialog,
      CurrencyPicker,
      DatePicker,
      ErrorAlert,
      QuotePicker,
      QuotePaymentTermCodePicker
    },

    data() {
      return {
        STEPS,
        dialog: false,
        loading: false,

        error: null as ISystemError | null,
        step: STEPS.QuoteSelector,
        contractData: {} as IContractData,

        selectedQuote: null as null | IQuote,

        isLoading: false,
        isContractFormValid: false,
        isQuoteSetupLoading: false,

        isEdit: false,

        QuoteState: QuoteState,

        requiredRule: [(v: string) => !!v || 'This field is required']
      };
    },

    computed: {
      ...mapGetters({
        getQuoteById: 'quotes/getQuoteById',
        getContractById: 'contract/getContractById'
      }),

      startDateRangeRule() {
        return (start: any) => (end?: Date | string) => {
          if (!start || !end) return true;
          return (
            moment(start).isSameOrAfter(end, 'day') ||
            'Activation cannot be after the expiry date'
          );
        };
      },

      endDateRangeRule() {
        return (end?: string | null) => (start?: string | null) => {
          if (!start || !end) return true;

          return (
            moment(end).isSameOrBefore(start, 'day') ||
            'Expiry cannot be before the activation date'
          );
        };
      }
    },

    methods: {
      ...mapActions({
        createContract: 'contract/createContract',
        updateContract: 'contract/updateContract'
      }),

      moveToContractBuilderStep() {
        if (this.validateContactForm()) {
          this.step = STEPS.ContractBuilder;
        }
      },

      setDefaultExpiryDate(date: string | null) {
        if (date && !this.contractData.expiry_date) {
          this.contractData.expiry_date = moment(date)
            .add(1, 'year')
            .subtract(1, 'day')
            .format('YYYY-MM-DD');
        }
      },

      onActivationDateInputHandler(date: string | null) {
        this.$nextTick(() => {
          this.$refs['endDatePicker'].$refs['datePicker'].validate();
        });
        this.clearErrors();
        this.setDefaultExpiryDate(date);
      },

      onExpiryDateInputHandler() {
        this.$nextTick(() => {
          this.$refs['startDatePicker'].$refs['datePicker'].validate();
        });

        this.clearErrors();
      },

      async saveContract() {
        if (this.validateContactForm()) {
          let contractInstance: IContract | null = null;

          try {
            contractInstance = new Contract(this.contractData);
          } catch (error) {
            if (SystemError.isSystemError(error)) {
              this.error = error;
            }
          }

          if (contractInstance != null) {
            this.isLoading = true;

            const saveAction = this.isEdit
              ? this.updateContract
              : this.createContract;

            let payload = contractInstance;

            if (this.isEdit) {
              payload = diffDeep(
                contractInstance,
                this.getContractById(contractInstance.id),
                true
              );
            }

            saveAction(payload)
              .then((contractId?: number) => {
                if (!this.isEdit) {
                  this.$router
                    .push({
                      name: 'contract-detail',
                      params: { id: String(contractId) }
                    })
                    .then(() => {
                      this.createSnack({
                        color: 'success darken-1',
                        type: 'info',
                        right: true,
                        msg: 'Contract created',
                        timeout: 4000
                      });
                    });
                } else {
                  this.close();
                }
              })
              .catch(error => {
                this.error = setErrorFromAxiosError(error);
              })
              .finally(() => {
                this.isLoading = false;
              });
          }
        }
      },

      async open(contract?: IContract) {
        this.clearErrors();

        this.dialog = true;

        this.loading = true;
        this.contractData = contract
          ? cloneDeep(contract)
          : {
              grace_period_days: 30
            };

        this.isEdit = !!contract?.id;

        this.selectedQuote = (contract?.quote as IQuote) ?? null;

        this.step = contract ? STEPS.ContractBuilder : STEPS.QuoteSelector;

        this.loading = false;
      },

      close() {
        this.dialog = false;
      },

      clearErrors() {
        this.error = null;
      },

      validateContactForm(): boolean {
        let valid = false;
        if (this.$refs.contractForm) {
          valid = this.$refs.contractForm.validate();
        }
        return valid;
      },

      async quoteInputHandler(quote: IQuote | null) {
        this.clearErrors();
        if (!this.isEdit && quote) {
          this.$nextTick(() => {
            this.setContractDataFromQuote(quote);
          });
        }
      },

      setContractDataFromQuote(quote: IQuote) {
        // clear quote related data on contract

        this.isQuoteSetupLoading = true;

        this.contractData.quote = quote.id;

        // Currency
        if (quote.currency) {
          this.contractData.currency = quote.currency;
        }

        // Customer
        if (quote.customer && quote.customer.identifier) {
          this.contractData.customer_account_number = quote.customer.identifier;
        }

        this.isQuoteSetupLoading = false;
      }
    }
  });
